/**
 * Sleek notification component for CEISA riwayat respon updates
 */
import React, { forwardRef, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import type { CeisaVisualNotification, RiwayatResponNotification } from '@hooks/useCeisaWebSocket';
import { useTheme } from '@contexts/ThemeContext';
import { useLanguage } from '@contexts/LanguageContext';
import type { LanguageCode } from '@constants/translations';

// Deck geometry (chosen in the stack simulator)
const CARD_WIDTH = 280;
const PEEK_COUNT = 3; // cards visible behind the front one
const PEEK_OFFSET = 10; // px each back card sticks out above the one in front
const PEEK_SCALE_STEP = 0.05;
const EXPANDED_GAP = 8;
const CLEAR_ALL_ZONE = 32; // hover zone above the expanded stack that holds "Close all"
const HOVER_LEAVE_GRACE_MS = 250;
const AGE_TICK_MS = 30000;

const getUraian = (n: RiwayatResponNotification) => n.uraian_respon || n.entries?.[0]?.uraian_singkat_respon;

/** NPD / SPJM — the red, urgent popups. */
const isUrgentRespon = (n: RiwayatResponNotification) => {
  const s = getUraian(n)?.toUpperCase() || '';
  return s.includes('NPD') || s.includes('SPJM');
};

const wibTime = new Intl.DateTimeFormat('en-GB', { timeZone: 'Asia/Jakarta', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' });
const wibDay = new Intl.DateTimeFormat('en-GB', { timeZone: 'Asia/Jakarta', day: 'numeric', month: 'short' });

/** "19:01 WIB", or "24 Sep, 19:01 WIB" when the popup has been open since another day. */
const formatReceivedAt = (ms: number, now: number) => {
  const time = `${wibTime.format(ms)} WIB`;
  return wibDay.format(ms) === wibDay.format(now) ? time : `${wibDay.format(ms)}, ${time}`;
};

const AGE_LABELS: Record<LanguageCode, { now: string; m: (n: number) => string; h: (n: number) => string; d: (n: number) => string }> = {
  id: { now: 'baru saja', m: (n) => `${n} mnt lalu`, h: (n) => `${n} jam lalu`, d: (n) => `${n} hari lalu` },
  en: { now: 'just now', m: (n) => `${n}m ago`, h: (n) => `${n}h ago`, d: (n) => `${n}d ago` },
  zh: { now: '刚刚', m: (n) => `${n}分钟前`, h: (n) => `${n}小时前`, d: (n) => `${n}天前` },
};

const formatAge = (ms: number, now: number, language: LanguageCode) => {
  const labels = AGE_LABELS[language] ?? AGE_LABELS.id;
  const minutes = Math.floor((now - ms) / 60000);
  if (minutes < 1) return labels.now;
  if (minutes < 60) return labels.m(minutes);
  const hours = Math.floor(minutes / 60);
  return hours < 24 ? labels.h(hours) : labels.d(Math.floor(hours / 24));
};

interface CeisaNotificationProps {
  notification: RiwayatResponNotification;
  receivedAt: number;
  now: number;
  /** Folded behind the front card: content hidden, only the edge shows. */
  behind?: boolean;
  /** Reports the card's natural height, independent of the slot it is stretched into. */
  onNaturalHeight?: (h: number) => void;
  onClose: () => void;
}

const CeisaNotificationItem = forwardRef<HTMLDivElement, CeisaNotificationProps>(({ notification, receivedAt, now, behind = false, onNaturalHeight, onClose }, ref) => {
  const { colors: themeColors } = useTheme();
  const { language } = useLanguage();
  const cardRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const onNaturalHeightRef = useRef(onNaturalHeight);
  onNaturalHeightRef.current = onNaturalHeight;

  useLayoutEffect(() => {
    const card = cardRef.current;
    const header = headerRef.current;
    const content = contentRef.current;
    if (!card || !header || !content) return;
    // header + body content + card borders; the body itself stretches with the slot
    const report = () => onNaturalHeightRef.current?.(header.offsetHeight + content.offsetHeight + (card.offsetHeight - card.clientHeight));
    report();
    const observer = new ResizeObserver(report);
    observer.observe(header);
    observer.observe(content);
    return () => observer.disconnect();
  }, []);

  // Handle both data formats: array-based (entries) and flat object
  const kodeRespon = notification.kode_respon || notification.entries?.[0]?.kode_respon;
  const uraianRespon = notification.uraian_respon || notification.entries?.[0]?.uraian_singkat_respon;
  const nomorRespon = notification.nomor_respon || notification.entries?.[0]?.nomor_respon;
  const waktuRespon = notification.waktu_respon || notification.entries?.[0]?.waktu_respon;
  const companyName = notification.company_name || notification.nama_perusahaan || notification.entries?.[0]?.nama_perusahaan;

  // Determine notification theme based on status
  const statusUpper = uraianRespon?.toUpperCase() || '';
  const isRed = statusUpper.includes('NPD') || statusUpper.includes('SPJM');
  const isGreen = statusUpper.includes('SPPB') || statusUpper.includes('NPE') || statusUpper.includes('SPTNP');
  const isOrange = statusUpper.includes('BILLING');

  // Format nomor aju with dashes: 000020-029326-20251017-005630
  const formatNomorAju = (nomorAju: string) => {
    if (!nomorAju) return '';
    // Remove any existing dashes
    const clean = nomorAju.replace(/-/g, '');
    // Format: 6 digits - 6 digits - 8 digits - 6 digits
    if (clean.length >= 26) {
      return `${clean.slice(0, 6)}-${clean.slice(6, 12)}-${clean.slice(12, 20)}-${clean.slice(20, 26)}`;
    }
    return nomorAju; // Return as-is if format doesn't match
  };

  const formattedAjuNo = formatNomorAju(notification.aju_no);
  // Urgent popups also show how long they have been waiting for someone to act
  const receivedLabel = isRed
    ? `${formatReceivedAt(receivedAt, now)} · ${formatAge(receivedAt, now, language)}`
    : formatReceivedAt(receivedAt, now);
  const onBrandHeader = !isRed && !isGreen && !isOrange;
  const contentFade = `transition-opacity duration-200 ${behind ? 'opacity-0' : 'opacity-100'}`;

  return (
    <div
      ref={(el) => {
        cardRef.current = el;
        if (typeof ref === 'function') ref(el);
        else if (ref) ref.current = el;
      }}
      className={`rounded-md shadow-lg overflow-hidden w-[280px] h-full flex flex-col ${
        isRed ? 'border-2 ring-2' :
        isGreen ? 'border-2 ring-2' :
        isOrange ? 'border-2 ring-2' :
        'border'
      }`}
      style={{
        backgroundColor: themeColors.card,
        borderColor: isRed ? themeColors.error : isGreen ? themeColors.success : isOrange ? themeColors.warning : themeColors.border,
        boxShadow: isRed ? `0 0 0 3px ${themeColors.error}20` : isGreen ? `0 0 0 3px ${themeColors.success}20` : isOrange ? `0 0 0 3px ${themeColors.warning}20` : undefined,
        backdropFilter: 'blur(10px)',
      }}
    >
      {/* Header with gradient */}
      <div
        ref={headerRef}
        className="px-2.5 py-1.5 flex items-center justify-between flex-shrink-0"
        style={{
          background: isRed ? `linear-gradient(to right, ${themeColors.error}, ${themeColors.error}dd)` :
                      isGreen ? `linear-gradient(to right, ${themeColors.success}, ${themeColors.success}dd)` :
                      isOrange ? `linear-gradient(to right, ${themeColors.warning}, ${themeColors.warning}dd)` :
                      `linear-gradient(to right, ${themeColors.primary}, ${themeColors.primaryHover})`,
        }}
      >
        <div className={`flex items-center gap-2 min-w-0 ${contentFade}`}>
          <div className={`w-5 h-5 flex-shrink-0 rounded-full bg-white/20 flex items-center justify-center backdrop-blur-sm ${
            isRed ? 'animate-pulse' : ''
          }`}>
            <svg
              className="w-3.5 h-3.5 text-white"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              {isRed ? (
                <path d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              ) : isGreen ? (
                <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              ) : isOrange ? (
                <path d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              ) : (
                <path d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
              )}
            </svg>
          </div>
          <h3 className="text-white font-semibold text-xs whitespace-nowrap">
            {isRed ? 'URGENT Alert' :
             isGreen ? 'Success Update' :
             isOrange ? 'Payment Required' :
             'CEISA Update'}
          </h3>
          <span
            className={`text-[10px] font-medium tabular-nums truncate ${onBrandHeader ? 'opacity-80' : 'text-white/80'}`}
            style={onBrandHeader ? { color: themeColors.card } : undefined}
            title="Waktu respon diterima"
          >
            · {receivedLabel}
          </span>
        </div>
        <button
          onClick={onClose}
          className={`text-white/60 hover:text-white transition-colors flex-shrink-0 ml-2 ${contentFade}`}
          aria-label="Close"
          tabIndex={behind ? -1 : 0}
        >
          <svg
            className="w-4 h-4"
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      {/* Content */}
      <div className={`flex-1 ${contentFade}`} style={{ backgroundColor: themeColors.background }}>
        <div ref={contentRef} className="px-2.5 py-1.5 space-y-1">
          {/* Company Name */}
          {companyName && (
            <div className="flex items-center gap-1.5">
              <div className="w-1 h-1 rounded-full flex-shrink-0" style={{ backgroundColor: themeColors.primary }} />
              <p className="text-[10px]" style={{ color: themeColors.textSecondary }}>
                <span className="font-semibold" style={{ color: themeColors.text }}>{companyName}</span>
              </p>
            </div>
          )}

          {/* Nomor Aju */}
          <div className="flex items-center gap-1.5">
            <div className="w-1 h-1 rounded-full flex-shrink-0" style={{ backgroundColor: themeColors.primary }} />
            <p className="text-[10px] truncate" style={{ color: themeColors.text }}>
              <span style={{ color: themeColors.textSecondary }}>AJU: </span>
              <span className="font-semibold">{formattedAjuNo}</span>
            </p>
          </div>

          {/* Status */}
          {uraianRespon && (
            <div className="flex items-center gap-1.5">
              <div className="w-1 h-1 rounded-full flex-shrink-0" style={{ backgroundColor: themeColors.primary }} />
              <p className="text-[10px] truncate" style={{ color: themeColors.text }}>
                <span style={{ color: themeColors.textSecondary }}>Status: </span>
                <span className="font-semibold">{uraianRespon}</span>
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
});

CeisaNotificationItem.displayName = 'CeisaNotificationItem';

interface CeisaNotificationContainerProps {
  notifications: CeisaVisualNotification[];
  onRemove: (id: string) => void;
}

/**
 * Stack order: urgent (NPD/SPJM) first, oldest urgent in front — it is the one
 * most likely still unhandled. Everything else follows, newest first.
 */
const orderForStack = (items: CeisaVisualNotification[]) => {
  const urgent = items.filter((n) => isUrgentRespon(n.notification)).sort((a, b) => a.receivedAt - b.receivedAt);
  const rest = items.filter((n) => !isUrgentRespon(n.notification)).sort((a, b) => b.receivedAt - a.receivedAt);
  return [...urgent, ...rest];
};

interface DismissTimer {
  handle: ReturnType<typeof setTimeout> | null;
  remaining: number;
  startedAt: number;
}

/**
 * Popups fold into an overlapping deck in the bottom-right corner; hovering
 * fans them out into a list (timers paused) with a "Close all" pill on top.
 */
export const CeisaNotificationContainer: React.FC<CeisaNotificationContainerProps> = ({
  notifications,
  onRemove,
}) => {
  const { colors: themeColors } = useTheme();
  const [hovered, setHovered] = useState(false);
  const [heights, setHeights] = useState<Record<string, number>>({});
  const [now, setNow] = useState(() => Date.now());
  const leaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const timersRef = useRef(new Map<string, DismissTimer>());
  const hoveredRef = useRef(false);
  const onRemoveRef = useRef(onRemove);
  onRemoveRef.current = onRemove;

  const ordered = useMemo(() => orderForStack(notifications), [notifications]);

  // --- auto-dismiss, paused while the stack is hovered ---
  const startTimer = useCallback((id: string) => {
    const t = timersRef.current.get(id);
    if (!t || t.handle || hoveredRef.current) return;
    t.startedAt = Date.now();
    t.handle = setTimeout(() => onRemoveRef.current(id), t.remaining);
  }, []);

  const pauseTimer = useCallback((id: string) => {
    const t = timersRef.current.get(id);
    if (!t?.handle) return;
    clearTimeout(t.handle);
    t.handle = null;
    // Leave a moment to read it after the mouse moves away
    t.remaining = Math.max(1500, t.remaining - (Date.now() - t.startedAt));
  }, []);

  useEffect(() => {
    const timers = timersRef.current;
    const live = new Set(notifications.map((n) => n.id));
    for (const n of notifications) {
      if (n.durationMs == null || timers.has(n.id)) continue;
      timers.set(n.id, { handle: null, remaining: n.durationMs, startedAt: 0 });
      startTimer(n.id);
    }
    for (const [id, t] of timers) {
      if (live.has(id)) continue;
      if (t.handle) clearTimeout(t.handle);
      timers.delete(id);
    }
  }, [notifications, startTimer]);

  useEffect(() => {
    const timers = timersRef.current;
    return () => {
      for (const t of timers.values()) {
        if (t.handle) clearTimeout(t.handle);
      }
      if (leaveTimerRef.current) clearTimeout(leaveTimerRef.current);
    };
  }, []);

  // Keep "x mnt lalu" fresh while something is on screen
  useEffect(() => {
    if (notifications.length === 0) return;
    setNow(Date.now());
    const handle = setInterval(() => setNow(Date.now()), AGE_TICK_MS);
    return () => clearInterval(handle);
  }, [notifications.length]);

  // Collapse once the stack is gone, so the next popup starts folded
  useEffect(() => {
    if (notifications.length === 0) {
      hoveredRef.current = false;
      setHovered(false);
    }
  }, [notifications.length]);

  const handleMouseEnter = () => {
    if (leaveTimerRef.current) clearTimeout(leaveTimerRef.current);
    if (hoveredRef.current) return;
    hoveredRef.current = true;
    setHovered(true);
    for (const id of timersRef.current.keys()) pauseTimer(id);
  };

  // Short grace period so brushing past the edge doesn't snap the stack shut
  const handleMouseLeave = () => {
    if (leaveTimerRef.current) clearTimeout(leaveTimerRef.current);
    leaveTimerRef.current = setTimeout(() => {
      hoveredRef.current = false;
      setHovered(false);
      for (const id of timersRef.current.keys()) startTimer(id);
    }, HOVER_LEAVE_GRACE_MS);
  };

  const setHeight = useCallback((id: string, h: number) => {
    setHeights((prev) => (prev[id] === h ? prev : { ...prev, [id]: h }));
  }, []);

  // --- deck geometry ---
  const expanded = hovered && ordered.length > 1;
  const frontHeight = heights[ordered[0]?.id] ?? 0;
  const offsets: number[] = [];
  let expandedHeight = 0;
  for (const [i, n] of ordered.entries()) {
    offsets.push(expandedHeight);
    expandedHeight += (heights[n.id] ?? 0) + (i < ordered.length - 1 ? EXPANDED_GAP : 0);
  }
  const collapsedHeight = frontHeight + Math.min(ordered.length - 1, PEEK_COUNT) * PEEK_OFFSET;
  const containerHeight = ordered.length === 0 ? 0 : expanded ? expandedHeight + CLEAR_ALL_ZONE : collapsedHeight;

  const handleClearAll = () => {
    for (const n of notifications) onRemove(n.id);
  };

  return (
    <motion.div
      className="fixed bottom-3 right-3 z-[9999]"
      style={{ width: CARD_WIDTH }}
      animate={{ height: containerHeight }}
      transition={{ type: 'spring', stiffness: 400, damping: 38 }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <AnimatePresence>
        {expanded && (
          <motion.button
            key="clear-all"
            type="button"
            onClick={handleClearAll}
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 6 }}
            transition={{ duration: 0.15 }}
            className="absolute right-0 top-0 rounded-full border px-2.5 py-0.5 text-[11px] shadow-sm transition-colors"
            style={{ backgroundColor: themeColors.card, borderColor: themeColors.border, color: themeColors.textSecondary }}
          >
            Tutup semua
          </motion.button>
        )}
      </AnimatePresence>

      <AnimatePresence initial={false}>
        {ordered.map(({ id, notification, receivedAt }, index) => {
          const depth = Math.min(index, PEEK_COUNT);
          const naturalHeight = heights[id];
          const behind = !expanded && index > 0;
          return (
            <motion.div
              key={id}
              className="absolute inset-x-0 bottom-0"
              style={{ transformOrigin: 'top center', zIndex: 100 - index, pointerEvents: behind ? 'none' : 'auto' }}
              initial={{ opacity: 0, y: 24, scale: 0.97 }}
              animate={{
                opacity: !expanded && index > PEEK_COUNT ? 0 : 1,
                y: expanded ? -offsets[index] : -depth * PEEK_OFFSET,
                scale: expanded ? 1 : 1 - depth * PEEK_SCALE_STEP,
                // Folded cards borrow the front card's height so their edges line up
                height: expanded ? naturalHeight ?? 'auto' : frontHeight || 'auto',
              }}
              exit={{ opacity: 0, x: 100, transition: { duration: 0.25 } }}
              transition={{ type: 'spring', stiffness: 400, damping: 38 }}
            >
              <CeisaNotificationItem
                notification={notification}
                receivedAt={receivedAt}
                now={now}
                behind={behind}
                onNaturalHeight={(h) => setHeight(id, h)}
                onClose={() => onRemove(id)}
              />
            </motion.div>
          );
        })}
      </AnimatePresence>
    </motion.div>
  );
};

export default CeisaNotificationContainer;
